import { useQuery } from '@tanstack/react-query';
import type { MyProfileDto } from '@/api/generated/model';
import { currentUserKeys, currentUserQueryOptions } from '@/lib/current-user';
import { useSessionStore } from '@/lib/session';

export { currentUserKeys };

/**
 * The signed-in user, fetched from the server.
 *
 * A query rather than a stored object, because the profile *is* server state:
 * name, agency and role all change server-side, and a copy in zustand would
 * start going stale immediately. It also cannot come from sign-in — better-auth
 * returns only id/email/name, with no `agencyId` and no `role`.
 *
 * Only runs when a token exists, so signed-out screens never fire a doomed
 * request. Never retried: this call is also how a bad token is discovered, and
 * the 401 handler in `@/api/client` has already cleared the session and
 * redirected by the time it would retry.
 */
export function useCurrentUserQuery() {
  const token = useSessionStore((state) => state.token);

  return useQuery({ ...currentUserQueryOptions(), enabled: token !== null });
}

/** The user, or null while loading / signed out. */
export function useCurrentUser(): MyProfileDto | null {
  return useCurrentUserQuery().data ?? null;
}

/**
 * Whether the signed-in user owns the agency.
 *
 * Inviting teammates and removing members are owner-only on the backend, so
 * gate those controls on this rather than showing buttons that always 403.
 * False while the profile is still loading — the safe direction.
 */
export function useIsOwner(): boolean {
  return useCurrentUser()?.role === 'owner';
}
